// src/pages/RecursosHumanos/Colaboradores/index.tsx
import React, { useMemo } from "react";
import Table from "../../../components/Table";
import TitleButtons from "../../../components/TitleButtons";
import { useEmpresasCliente } from "./colaboradoresData"; // mantém o nome por compatibilidade
import { useNavigate } from "react-router-dom";

// =============== Tipos ===============
type Colaborador = {
  id?: string | number;
  nome_completo: string;
  cpf?: string | null;
  setor?: string | null; // exibido como "Departamento"
  data_de_nascimento?: string | null; // ISO (ainda existe no tipo, mas não exibimos)
  status_atual?: string | null; // novo campo exibido na tabela
};

// =============== Utils ===============
function onlyDigits(s: string) {
  return String(s || "").replace(/\D+/g, "");
}

function formatCPF(cpf?: string | null) {
  const d = onlyDigits(cpf ?? "")
    .padStart(11, "0")
    .slice(-11);
  // 000.000.000-00
  return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
}

/** Aceita { data: [...] } ou array direto, e normaliza chaves */
function normalizeColaboradores(raw: any): Colaborador[] {
  const arr = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.data)
    ? raw.data
    : [];
  return (arr as any[]).map((x) => ({
    id: x.id ?? x.colaborador_id ?? x.user_id ?? undefined,
    nome_completo:
      x.nome_completo ?? x.nomeCompleto ?? x.nome ?? x.full_name ?? "",
    cpf: x.cpf ?? x.CPF ?? x.documento ?? null,
    setor: x.setor ?? x.departamento ?? x.area ?? null,
    data_de_nascimento:
      x.data_de_nascimento ?? x.nascimento ?? x.birth_date ?? null,
    status_atual:
      x.status_atual ?? x.statusAtual ?? x.status ?? x.situacao ?? null,
  }));
}

// Normaliza status para comparação (remove acento, espaços, hífen etc.)
function normalizeStatusText(raw: string | null | undefined): string {
  if (!raw) return "";
  let s = String(raw).trim().toLowerCase();

  // tira acentos básicos
  s = s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // remove diacríticos
    .replace(/[\s_-]+/g, " "); // espaços / hífens / underscores viram espaço simples

  return s;
}

// =============== Página ===============
const Colaboradores: React.FC = () => {
  // 1) headers: adiciona uma coluna oculta "ID" na posição 0
  //    Troca "Aniversário" por "Status"
  const headers = useMemo(
    () => ["", "Nome Completo", "CPF", "Departamento", "Status"],
    []
  );

  // 2) columnWidths: primeira coluna "0px" (oculta)
  const columnWidths = useMemo(
    () => ["0px", "420px", "180px", "260px", "160px"],
    []
  );

  // Hook de dados (mantém o nome, mas agora entrega colaboradores)
  const hook = useEmpresasCliente?.();
  const data = normalizeColaboradores((hook as any)?.data ?? hook);

  // 3) rows: injeta o ID como primeira célula (oculta), seguido das colunas visíveis
  const rows = useMemo<React.ReactNode[][]>(() => {
    return data.map((c) => [
      String(c.id ?? ""), // <-- coluna 0 (oculta)
      c.nome_completo || "-",
      c.cpf ? formatCPF(c.cpf) : "-",
      c.setor || "-",
      c.status_atual || "-", // <-- agora exibe o status
    ]);
  }, [data]);

  const navigate = useNavigate();
  const idIdx = 0; // índice da coluna oculta

  // ===== Estilo da célula de STATUS (cores pastéis) =====
  const getStatusCellStyle = (
    _rowIndex: number,
    colIndex: number,
    value: string | number | React.ReactNode,
    _originalIndex: number,
    _rowData: (string | number | React.ReactNode)[],
    headerTitle: string
  ): React.CSSProperties | undefined => {
    // Garante que é a coluna "Status"
    if (headerTitle !== "Status") return undefined;
    if (colIndex !== 4) return undefined; // segurança extra

    if (typeof value !== "string" && typeof value !== "number") {
      return undefined;
    }

    const txt = normalizeStatusText(String(value));

    // paleta pastel
    const greenPastel = "#DCFCE7"; // disponível
    const redPastel = "#FEE2E2"; // atestado/licença/férias
    const orangePastel = "#FEF3C7"; // em serviço

    let bg: string | null = null;

    // disponível
    if (txt.startsWith("disponivel") || txt.startsWith("disponivel")) {
      bg = greenPastel;
    }
    // atestado médico (aceita variações: "atestado medico", "atestado-medico", etc.)
    else if (txt.includes("atestado medico")) {
      bg = redPastel;
    }
    // licença maternidade
    else if (txt.includes("licenca maternidade")) {
      bg = redPastel;
    }
    // férias
    else if (txt === "ferias" || txt.startsWith("ferias ")) {
      bg = redPastel;
    }
    // em serviço
    else if (txt === "em servico" || txt.startsWith("em servico")) {
      bg = orangePastel;
    }

    if (!bg) return undefined;

    return {
      backgroundColor: bg,
      color: "#111",
      fontWeight: 500,
      textTransform: "uppercase",
    };
  };

  return (
    <div>
      <TitleButtons
        title="Recursos Humanos - Colaboradores"
        buttons={[
          {
            label: "Registrar Colaborador",
            onClick: () =>
              navigate("/recursos-humanos/colaboradores/registrar-colaborador"),
            variant: "primary",
          },
        ]}
      />

      <Table
        headers={headers}
        rows={rows}
        columnWidths={columnWidths}
        responsiveWidths={{
          1300: { 0: "0px", 1: "360px", 2: "160px", 3: "220px", 4: "120px" },
          1000: { 0: "0px", 1: "320px", 2: "150px", 3: "0px", 4: "110px" },
        }}
        onDoubleClick={(_viewIndex, rowData) => {
          const id = String(rowData[idIdx] ?? "");
          if (id) {
            navigate(`/recursos-humanos/colaborador/${encodeURIComponent(id)}`);
          }
        }}
        enableCellColors={true}
        getCellStyle={getStatusCellStyle}
      />
    </div>
  );
};

export default Colaboradores;
